import { Link } from 'react-router-dom'
import Seo from '../components/Seo.jsx'
import Related from '../components/Related.jsx'
import FaqBlock from '../components/FaqBlock.jsx'
import { faqLd } from '../lib/faq.js'

/*
 * /peptide-dosage-chart — a keyword-targeted resource page for "peptide dosage
 * chart". Every vial size gets a branded infographic (for Google Image search)
 * AND a real HTML table (for text SEO, AI answers, and accessibility). Tables are
 * computed from the same math as the calculator, so they can't drift.
 */
const VIALS = [5, 10, 20, 30, 40, 60] // mg
const WATERS = [1, 2, 3] // mL of bacteriostatic water
const DOSES = [100, 250, 500, 1000, 2000, 5000] // mcg

// units to draw on a U-100 syringe = dose(mcg) × water(mL) ÷ (vial(mg) × 10)
const unitsFor = (mg, mcg, ml) => (mcg * ml) / (mg * 10)
const trim = (n) => String(Math.round(n * 10) / 10)
const doseLabel = (mcg) => (mcg >= 1000 ? `${mcg.toLocaleString()} mcg (${mcg / 1000} mg)` : `${mcg} mcg`)

const FAQ = [
  { q: 'What is a peptide dosage chart?', a: 'A peptide dosage chart shows how many units to draw on an insulin syringe for a given dose, based on your vial size and how much bacteriostatic water you added. It turns the reconstitution math — milligrams in the vial, millilitres of water, and micrograms per dose — into a simple “draw to this many units” lookup.' },
  { q: 'How do I read the chart?', a: 'Pick the table for your vial size, find your target dose down the left, then read across to the column for how much bacteriostatic water you added (1, 2, or 3 mL). The number in that cell is the units to draw on a U-100 insulin syringe.' },
  { q: 'Does adding more or less water change my dose?', a: 'No. The amount of bacteriostatic water only changes the concentration — and therefore how many units you draw — not the actual amount of peptide in each dose. More water means more units per dose (easier to measure small amounts); less water means fewer units.' },
  { q: 'What if my exact dose or vial size isn’t on the chart?', a: 'Use the calculator — enter any vial size, water amount, and dose and it returns the exact units to draw, the concentration, and doses per vial, with a syringe diagram. The charts here cover the most common combinations at a glance.' },
]

function ChartTable({ mg }) {
  return (
    <div className="dchart-scroll">
      <table className="dchart">
        <caption className="dchart-cap">{mg} mg vial · units to draw on a U-100 insulin syringe</caption>
        <thead>
          <tr>
            <th scope="col">Dose</th>
            {WATERS.map((w) => <th scope="col" key={w}>{w} mL water</th>)}
          </tr>
        </thead>
        <tbody>
          {DOSES.map((mcg) => (
            <tr key={mcg}>
              <th scope="row">{doseLabel(mcg)}</th>
              {WATERS.map((w) => {
                const u = unitsFor(mg, mcg, w)
                const cls = u > 100 ? 'over' : u > 0 && u < 2 ? 'tiny' : ''
                return <td key={w} className={cls}>{u > 100 ? `${trim(u)} ⚠` : `${trim(u)} u`}</td>
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function PeptideDosageChart() {
  const imgList = VIALS.map((mg) => ({
    '@type': 'ImageObject',
    contentUrl: `https://calculatemypeptides.com/images/peptide-dosage-chart-${mg}mg.png`,
    name: `Peptide dosage chart — ${mg} mg vial`,
    description: `Insulin-syringe units to draw for a ${mg} mg peptide vial by dose and bacteriostatic water amount.`,
  }))

  return (
    <main className="page">
      <Seo
        title="Peptide Dosage & Dosing Chart — Units by Vial Size"
        description="Free peptide dosage chart (aka dosing chart): insulin-syringe units to draw for 5, 10, 20, 30, 40 and 60 mg vials by dose and bacteriostatic water. Printable charts plus a live calculator."
        image="/images/peptide-dosage-chart-5mg.png"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Peptide Dosage Chart', path: '/peptide-dosage-chart' }]}
        jsonLd={[faqLd('Peptide dosage chart — FAQ', FAQ), { '@context': 'https://schema.org', '@type': 'ItemList', itemListElement: imgList.map((img, i) => ({ '@type': 'ListItem', position: i + 1, item: img })) }]}
      />
      <div className="container page-narrow">
        <div style={{ marginBottom: 18 }}>
          <span className="pill blue" style={{ marginBottom: 8 }}>Reference</span>
          <h1 className="section-title">Peptide Dosage Chart</h1>
          <p className="section-sub" style={{ maxWidth: '64ch' }}>
            A peptide dosage chart (also called a peptide dosing chart) showing how many units to draw on an insulin
            syringe for each dose — by vial size and how much bacteriostatic water you added. Find your vial below,
            or run any combination in the{' '}
            <Link to="/calculator">peptide calculator</Link>. Educational only, not medical advice.
          </p>
        </div>

        <nav className="dchart-jump no-print" aria-label="Jump to vial size">
          <span className="dchart-jump-lbl">Jump to:</span>
          {VIALS.map((mg) => <a key={mg} href={`#chart-${mg}mg`} className="dchart-jump-link">{mg} mg</a>)}
          <button type="button" className="btn btn-soft btn-sm dchart-print" onClick={() => window.print()}>🖨 Print / Save as PDF</button>
        </nav>

        <p className="dchart-how">
          <strong>How to read it:</strong> find your <em>dose</em> down the left, then read across to how much{' '}
          <em>bacteriostatic water</em> you added. The cell is the units to draw on a U-100 syringe. A{' '}
          <span className="dchart-key tiny">tiny</span> value is hard to measure — add more water; a{' '}
          <span className="dchart-key over">⚠</span> value won’t fit a 1 mL (100-unit) syringe — add less.
        </p>

        {VIALS.map((mg) => (
          <section key={mg} id={`chart-${mg}mg`} className="dchart-section">
            <h2 className="pep-h2">{mg} mg vial — peptide dosage chart</h2>
            <img
              className="dchart-img"
              src={`/images/peptide-dosage-chart-${mg}mg.png`}
              alt={`Peptide dosage chart for a ${mg} mg vial — insulin-syringe units to draw by dose and bacteriostatic water amount, from CalculateMyPeptides`}
              width="1300" height="768" loading="lazy"
            />
            <div className="dchart-actions no-print">
              <a className="btn btn-soft btn-sm" href={`/images/peptide-dosage-chart-${mg}mg.png`} download={`peptide-dosage-chart-${mg}mg.png`}>⬇ Download this chart</a>
            </div>
            <ChartTable mg={mg} />
          </section>
        ))}

        <section className="page-narrow" style={{ marginTop: 8 }}>
          <p>
            These charts assume a <strong>U-100 insulin syringe</strong> (100 units = 1 mL). The amount of
            bacteriostatic water never changes your dose — only how many units you draw for it. For a vial size,
            water amount, or dose not shown here, the <Link to="/calculator">calculator</Link> gives the exact units
            and a syringe diagram, and each compound has its own <Link to="/charts">per-peptide dosing chart</Link>.
          </p>
          <FaqBlock title="Peptide dosage chart FAQ" items={FAQ} />
        </section>

        <Related
          title="Also useful"
          links={[
            { to: '/calculator', label: 'Peptide calculator', desc: 'Exact units for any vial' },
            { to: '/charts', label: 'Per-peptide charts', desc: 'Dosing by compound' },
            { to: '/articles/how-to-reconstitute-glp1-peptides', label: 'Reconstitution guide', desc: 'How the math works' },
            { to: '/glossary', label: 'Glossary', desc: 'Every term defined' },
          ]}
        />
      </div>
    </main>
  )
}
